/**
 * 연락처 패널 — MailPage 사이드바에서 "연락처" 활성 시 우측 패널에 표시.
 *
 * 조작 모델 (행 단위 아이콘 버튼을 없앤 이유)
 *   예전엔 행 끝에 수정/삭제 아이콘이 붙어 있었다. 행마다 작은 과녁을 두 개씩 두면 오조작이 쉽고,
 *   여러 건을 지우려면 한 건씩 확인 대화상자를 반복해야 했다. 지금은 표에서 익숙한 방식으로 바꿨다:
 *     - 수정 : 행을 클릭 → 모달
 *     - 삭제 : 좌측 체크박스로 고른 뒤 일괄 삭제
 *
 * 권한 (서버가 내려주는 ownedByMe 하나로만 판단)
 *   화면은 스코프를 보고 권한을 추측하지 않고 서버가 내려준 ownedByMe 만 본다 —
 *   추측하게 두면 서버 규칙이 바뀔 때마다 화면과 어긋난다.
 *   내 것이 아니면 체크박스가 아예 없고, 행을 눌러도 조회 전용 모달이 열린다.
 *
 *   ※ 알려진 간극(미해결) — 지금은 그 ownedByMe 가 서버 규칙을 다 담지 못한다.
 *   서버(ContactService.canWrite)는 '등록자 본인 + <b>공용 한정</b> 관리자' 를 허용하는데
 *   ownedByMe 는 순수 owner 비교라 관리자가 남의 공용 연락처를 봐도 false 가 내려온다.
 *   즉 서버는 되는데 화면이 감춘다(관리자의 공용 주소록 정리가 UI 로 불가능하다).
 *   메우려면 DTO 가 쓰기 가능 여부를 따로 실어야 하고, 아래 selectable 의 공용 차단까지
 *   함께 풀어야 한다 — 판단이 필요한 별건이라 이번 변경에서는 손대지 않았다.
 *
 * '사내연락처' 탭은 성격이 다르다
 *   나머지 두 탭이 contact 테이블(사람이 등록한 주소록)을 본다면, 사내연락처는 <b>직원 명부</b>를
 *   비춘다(GET /contacts/company). 그래서 이 탭에서만:
 *     - 열 구성이 다르다   : 부서·직위·직책이 있고, 회사·회사번호가 없다
 *     - 등록·삭제가 없다   : 직원 정보의 입구는 인사 화면 하나여야 한다. 여기서도 고칠 수 있게
 *                            하면 같은 데이터에 입구가 둘이 되고, 어느 쪽이 진짜인지 알 수 없어진다
 *     - 행 클릭 시 CompanyMemberDialog(조회 전용)가 열린다
 *   탭 값 "COMPANY" 는 서버의 ContactScope 값이 <b>아니다</b> — 화면에서만 쓰는 탭 식별자다.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
    Box, Checkbox, IconButton, Paper, Stack, Tab, TableBody, TableCell, TableHead,
    TableRow, Tabs, TextField, Tooltip, Typography,
} from "@mui/material";
import DeleteRoundedIcon    from "@mui/icons-material/DeleteRounded";
import SearchRoundedIcon    from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon   from "@mui/icons-material/RefreshRounded";

import ContactEditDialog   from "./ContactEditDialog";
import CompanyMemberDialog from "./CompanyMemberDialog";
import ActionButton from "../../../../component/common/ActionButton";
import TablePaginator, { DEFAULT_PAGE_SIZE, TableSizeSelect } from "../../../../component/common/TablePaginator";
import ResizableTable from "../../../../component/common/ResizableTable";
import { mailApi }       from "../../api/mailApi";
import { ds }            from "../../../../styles/designSystem";
import { notify }        from "../../../../util/toast";
import { confirm }       from "../../../../util/confirmDialog";
import { formatPhone }      from "../../../../util/phone";
import LoadingDots from "../../../../component/common/LoadingDots";

/** 한 번에 지우면 되돌리기 어려운 규모 — 이 수를 넘으면 확인 문구를 더 강하게 띄운다. */
const BULK_DELETE_WARN_THRESHOLD = 50;

/**
 * @param initialScope 사이드바에서 '내 연락처'(PERSONAL) / '공용 연락처'(PUBLIC) 중 무엇을 눌러 들어왔는지.
 *   누른 항목과 처음 보이는 탭이 어긋나면 라벨이 거짓말을 하는 셈이라, 진입 지점이 탭을 결정한다.
 */
export default function ContactsPanel({ onComposeMail, initialScope = "PERSONAL" }) {
    const [scope, setScope] = useState(initialScope); // PERSONAL/COMPANY/PUBLIC
    const [q, setQ] = useState("");
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [editDlg, setEditDlg] = useState(null);   // null = 닫힘, {} = 신규, {...} = 보기/수정
    const [memberDlg, setMemberDlg] = useState(null); // 사내연락처 탭의 직원 상세(조회 전용)
    const [selected, setSelected] = useState([]);   // 체크된 연락처 id
    const [deleting, setDeleting] = useState(false);

    /** 사내연락처 = 직원 명부를 비추는 탭. 연락처 CRUD 가 성립하지 않는 유일한 탭이라 분기가 잦다. */
    const isCompany = scope === "COMPANY";


    // 공용 등록은 2026-08-07 서버(ContactService.create)에서 전 직원으로 열렸다. 화면에 같은 제한을
    // 한 벌 더 남겨두면 서버가 허용하는 기능이 사용자에게는 <b>없는 기능</b>이 된다 — 버튼이 아예
    // 안 보이니 "권한이 없다" 가 아니라 "그런 건 원래 안 된다" 로 읽혀 아무도 문제 제기를 못 한다.
    // 그래서 화면 제한도 함께 걷어낸다. (등록 후 수정·삭제는 여전히 서버의 쓰기 규칙을 따른다)
    // 사내연락처만 남는 제한이다 — 여긴 연락처가 아니라 직원 명부라 애초에 등록 대상이 아니다.
    const canCreate = !isCompany;

    /**
     * 공용·사내연락처 탭에서는 선택·일괄삭제를 아예 제공하지 않는다.
     *
     * <p>공용은 전 직원이 함께 쓰는 주소록이라 한 사람의 오조작이 조직 전체에 번진다.
     * 시더가 owner 를 특정 직원으로 몰아 넣어둔 탓에 그 계정에서는 3159건 전부가 '내 것' 으로 보이는데,
     * 전체선택 한 번이면 사내 주소록이 통째로 사라진다. 그래서 <b>탭 자체로</b> 막는다 —
     * 개별 소유권 판정보다 앞선, 더 단순하고 확실한 방어선이다.
     * (공용 항목 정리는 필요하면 관리자 도구로 별도 처리한다.)
     *
     * <p>사내연락처는 이유가 다르다 — 지울 연락처가 없다. 행이 가리키는 건 직원이고, 삭제는
     * 인사 화면의 일이다. 여기서 지울 수 있으면 주소록 정리인 줄 알고 사람을 지우게 된다.
     */
    const selectable = scope === "PERSONAL";

    // 이 페이지에서 내가 손댈 수 있는 행 — 체크박스가 붙는 대상.
    const ownedRows = useMemo(
        () => (selectable ? rows.filter(r => r.ownedByMe) : []),
        [rows, selectable]);
    const allOwnedSelected = ownedRows.length > 0 && ownedRows.every(r => selected.includes(r.id));
    const someOwnedSelected = ownedRows.some(r => selected.includes(r.id)) && !allOwnedSelected;

    const load = async (overrideQ = null, overridePage = null, overrideScope = null) => {
        const s  = overrideScope != null ? overrideScope : scope;
        const qq = overrideQ     != null ? overrideQ     : q;
        const pp = overridePage  != null ? overridePage  : page;
        setLoading(true);
        try {
            // 사내연락처만 다른 엔드포인트를 본다 — 연락처가 아니라 직원 명부라서 응답 항목도 다르다.
            const res = s === "COMPANY"
                ? await mailApi.listCompanyContacts(qq, pp, size)
                : await mailApi.listContacts(s, qq, pp, size);
            const data = res?.data ?? res;
            setRows(data?.content || []);
            setTotal(data?.totalElements || 0);
        } catch {
            setRows([]);
            setTotal(0);
        } finally { setLoading(false); }
        // 목록이 바뀌면 선택은 버린다 — 화면에 없는 행이 선택된 채로 남아 있으면
        // 사용자가 "무엇을 지우는지" 확인할 수 없다.
        setSelected([]);
    };

    // 마운트 시 1회 + q 변경 시 debounce 1회 — 두 useEffect 가 중복 트리거되지 않도록 단일화.
    useEffect(() => {
        const t = setTimeout(() => { setPage(0); load(q, 0); }, q ? 300 : 0);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [q]);

    // 이미 연락처 화면에 있는 상태에서 사이드바의 다른 항목('내 연락처'↔'공용 연락처')을 누른 경우.
    // 이때는 패널이 언마운트되지 않으므로 useState 초기값만으로는 탭이 바뀌지 않는다 — 눌러도 아무 일이
    // 안 일어나는 것처럼 보이는 지점이라 명시적으로 따라가게 한다.
    useEffect(() => {
        setScope(prev => (prev === initialScope ? prev : initialScope));
        setPage(0);
        load(q, 0, initialScope);
        // eslint-disable-next-line
    }, [initialScope]);

    const toggleOne = (id) => {
        setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    /** 헤더 체크박스 — 내 것만 전체 선택/해제. 남의 행은 애초에 대상이 아니다. */
    const toggleAllOwned = () => {
        setSelected(allOwnedSelected ? [] : ownedRows.map(r => r.id));
    };

    const handleBulkDelete = async () => {
        if (selected.length === 0) return;
        const many = selected.length >= BULK_DELETE_WARN_THRESHOLD;
        const ok = await confirm({
            title: `연락처 ${selected.length}건 삭제`,
            message: many
                ? `${selected.length}건을 한 번에 삭제합니다. 되돌리려면 하나씩 다시 등록해야 합니다. 정말 진행할까요?`
                : `선택한 ${selected.length}건을 삭제할까요?`,
            confirmLabel: "삭제",
            variant: "danger",
        });
        if (!ok) return;

        setDeleting(true);
        try {
            const res = await mailApi.bulkDeleteContacts(selected);
            const r = res?.data ?? res ?? {};
            const deleted = r.deleted ?? 0;
            const skipped = r.skippedNotOwned ?? 0;
            // 서버가 권한 없는 항목을 건너뛸 수 있으므로 결과를 그대로 전한다 —
            // "삭제했습니다" 만 띄우면 실제로 안 지워진 게 있어도 사용자가 모른다.
            // 문구는 '본인 것이 아니라서' 라고 단정하지 않는다 — 서버 규칙이 owner 단독이 아니라
            // '등록자 본인 + 공용 한정 관리자' 라, 관리자에게는 남의 공용 연락처도 지워진다.
            // 화면이 이유를 좁게 단정하면 실제로 지워진 건에 대해 거짓말을 하게 된다.
            notify(
                skipped > 0
                    ? `${deleted}건을 삭제했습니다. ${skipped}건은 삭제 권한이 없어 제외했습니다.`
                    : `${deleted}건을 삭제했습니다.`,
                skipped > 0 ? "warning" : "success");
            await load();
        } catch (e) {
            notify(e?.response?.data?.message || "삭제 실패", "error");
        } finally { setDeleting(false); }
    };

    const COLS = 7;   // 체크박스 + 이름·이메일·휴대폰·회사번호·회사·직책

    return (
        // 스크롤은 '이 패널' 이 담당한다 — 표는 행 수만큼 자연 높이로 늘어나고 내부 스크롤을 만들지 않는다.
        // (시스템 공통 규칙: 표는 절대 자기 안에서 스크롤하지 않는다. 그래야 헤더 틀고정도 이 컨테이너 기준으로 붙는다.)
        <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column",
                   overflow: "auto", ...ds.hiddenScrollbar }}>
            {/* 헤더 */}
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                {/* 사내연락처는 세는 대상이 사람이라 단위가 다르다 — '3159건' 은 주소록 항목 수로 읽히고,
                    명부에서는 그게 곧 재직 인원이라 잘못된 숫자처럼 보인다. */}
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a", flex: 1 }}>
                    연락처 <Typography component="span" sx={{ ml: 1, fontSize: 12, color: "#94a3b8" }}>
                        {total}{isCompany ? "명" : "건"}
                    </Typography>
                </Typography>

                {/* 선택이 있을 때만 삭제 버튼이 나타난다 — 평소엔 파괴적 조작이 눈에 띄지 않는 편이 안전하다. */}
                {selected.length > 0 && (
                    <ActionButton
                        variant="delete"
                        startIcon={<DeleteRoundedIcon sx={{ fontSize: 16 }} />}
                        onClick={handleBulkDelete}
                        loading={deleting}
                        label={`선택 ${selected.length}건 삭제`}
                    />
                )}
                {/* N개씩 보기 — 표 우측 상단 고정(공용 컨트롤) */}
                <TableSizeSelect value={size} onChange={(n) => { setSize(n); setPage(0); load(q, 0); }} />
                <Tooltip title="새로고침">
                    <IconButton size="small" onClick={() => load()}>
                        <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
                {canCreate && (
                    <ActionButton variant="add" onClick={() => setEditDlg({ scope })} label="새 연락처" />
                )}
            </Stack>

            {/* 탭 — 앞의 셋은 주소가 '어디서 오는가' 순으로 좁은 것부터 넓은 것으로.
                  개인      : 내가 등록한 나만의 주소록
                  사내연락처 : 회사가 주는 직원 명부(등록·삭제 없음 — 아래 isCompany 분기)
                  공용연락처 : 전 직원이 함께 채우는 외부 업체 주소록
                '메일 수신 그룹' 은 여기 없다 — 주소록이 아니라 <b>발송 묶음</b>(누구를 어느 칸에
                넣을지)이라, 이 탭 줄의 규칙(검색·표·페이징)이 하나도 성립하지 않는다.
                메일 화면의 독립 뷰로 뺐다(MailPage 의 view="groups").
                (팀 탭은 2026-08-08 폐지 — ContactScope 주석 참고) */}
            <Tabs value={scope}
                onChange={(e, v) => { setScope(v); setPage(0); load(q, 0, v); }}
                sx={{ minHeight: 36, mb: 1.5,
                      "& .MuiTab-root": { minHeight: 36, fontSize: 13, fontWeight: 600, textTransform: "none" } }}>
                <Tab value="PERSONAL" label="개인" />
                <Tab value="COMPANY"  label="사내연락처" />
                <Tab value="PUBLIC"   label="공용연락처" />
            </Tabs>

            {/* 검색 */}
            <TextField
                fullWidth size="small"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={isCompany
                    ? "이름 / 부서 / 직위 / 직책 / 사번 / 이메일 / 전화 검색"
                    : "이름 / 이메일 / 회사 / 전화 검색"}
                InputProps={{
                    startAdornment: <SearchRoundedIcon sx={{ fontSize: 18, color: "#94a3b8", mr: 1 }} />,
                }}
                sx={{ mb: 2 }}
            />

            {/* 테이블 */}
            {/* 테두리만 그리는 껍데기 — 스크롤은 위의 패널 루트가 한다. clip 은 잘라내되
                스크롤 컨테이너를 만들지 않아 헤더 틀고정을 깨지 않는다. */}
            <Paper variant="outlined" sx={{
                borderColor: "#e2e8f0", overflowX: "clip", overflowY: "visible", flexShrink: 0,
            }}>
                <ResizableTable
                    size="small"
                    sx={ds.tableSx}
                    // 탭이 바뀌면 열 비율을 다시 잰다 — 첫 열이 체크박스↔번호로 성격이 바뀌는데
                    // 열 '개수' 는 그대로라, 이게 없으면 체크박스용 좁은 폭이 번호 열에 그대로 적용돼 잘린다.
                    resetKey={scope}
                >
                    <TableHead>
                        <TableRow>
                            {/* 첫 열은 하나만 쓴다 — 선택 가능한 탭이면 체크박스, 아니면 번호.
                                체크박스를 숨기면서 열만 남기면 폭만 차지하는 빈 칸이 생긴다. */}
                            {selectable ? (
                                // 48px = MUI 의 padding="checkbox" 기본값이자 body 셀과 같은 값(예전엔 44 였다).
                                // ResizableTable 의 바닥폭(minColWidth=56px)보다 좁으므로 이 열은 비율 배분에서
                                // 빠지고 늘 56px 로 고정된다 — 체크박스 실측 필요폭 42px 대비 여유가 충분하다.
                                <TableCell padding="checkbox" sx={{ width: 48 }}>
                                    {/* 내 것이 하나도 없는 페이지엔 전체선택도 의미가 없다. */}
                                    {ownedRows.length > 0 && (
                                        <Tooltip title="내가 등록한 연락처 전체 선택">
                                            <Checkbox
                                                size="small"
                                                checked={allOwnedSelected}
                                                indeterminate={someOwnedSelected}
                                                onChange={toggleAllOwned}
                                            />
                                        </Tooltip>
                                    )}
                                </TableCell>
                            ) : (
                                // 번호는 4자리(1000번대)까지 안 잘리게 — 자연폭 측정 시 이 값이 비율의 기준이 된다.
                                <TableCell align="center" sx={{ width: 64, minWidth: 64 }}>번호</TableCell>
                            )}
                            {/* 열 구성은 탭이 무엇을 비추느냐를 따라간다 — 사내연락처는 직원이라
                                '회사/회사번호' 대신 '부서/직위/직책' 이 온다. 열 개수는 같게 맞춰
                                (둘 다 7) 탭을 오갈 때 표가 크게 흔들리지 않게 했다. */}
                            {isCompany ? (
                                <>
                                    <TableCell>이름</TableCell>
                                    <TableCell>부서</TableCell>
                                    <TableCell>직위</TableCell>
                                    <TableCell>직책</TableCell>
                                    <TableCell>이메일</TableCell>
                                    <TableCell>휴대폰</TableCell>
                                </>
                            ) : (
                                <>
                                    <TableCell>이름</TableCell>
                                    <TableCell>이메일</TableCell>
                                    <TableCell>휴대폰</TableCell>
                                    <TableCell>회사번호</TableCell>
                                    <TableCell>회사</TableCell>
                                    <TableCell>직책</TableCell>
                                </>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={COLS} align="center" sx={{ py: 4, color: "#94a3b8" }}><LoadingDots/></TableCell></TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow><TableCell colSpan={COLS} align="center" sx={{ py: 6, color: "#94a3b8", fontSize: 13 }}>
                                {q ? "검색 결과가 없습니다"
                                   : isCompany ? "표시할 직원이 없습니다."
                                   : "등록된 연락처가 없습니다. 새 연락처로 시작하세요."}
                            </TableCell></TableRow>
                        ) : rows.map((r, idx) => {
                            const checked = selected.includes(r.id);
                            // 페이지를 넘겨도 이어지는 통 번호 — 3천 건짜리 목록에서 "몇 번째" 를 말할 수 있게.
                            const rowNo = page * size + idx + 1;
                            return (
                                <TableRow
                                    key={r.id}
                                    hover
                                    selected={checked}
                                    // 행 클릭 = 열기. 사내연락처는 직원 상세(조회 전용),
                                    // 나머지는 연락처 모달(내 것이면 수정, 아니면 조회 전용).
                                    onClick={() => (isCompany ? setMemberDlg(r) : setEditDlg(r))}
                                    sx={{ cursor: "pointer" }}
                                >
                                    {selectable ? (
                                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                            {/* ★ 내가 등록한 연락처에만 체크박스가 보인다 — 남의 것은 고를 수조차 없다. */}
                                            {r.ownedByMe && (
                                                <Checkbox size="small" checked={checked} onChange={() => toggleOne(r.id)} />
                                            )}
                                        </TableCell>
                                    ) : (
                                        <TableCell align="center" sx={{ fontSize: 12, color: "#94a3b8" }}>
                                            {rowNo}
                                        </TableCell>
                                    )}
                                    {isCompany ? (
                                        <>
                                            <TableCell sx={{ fontSize: 13 }}>{r.name || "-"}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{r.department || "-"}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{r.position || "-"}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{r.duty || "-"}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{r.email || "-"}</TableCell>
                                            {/* 직원 휴대폰은 하이픈 없이 저장된 값이 섞여 있다 — 표시할 때 통일한다. */}
                                            <TableCell sx={{ fontSize: 13 }}>{r.phone ? formatPhone(r.phone) : "-"}</TableCell>
                                        </>
                                    ) : (
                                        <>
                                            <TableCell sx={{ fontSize: 13 }}>{r.name || "-"}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{r.email || "-"}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{r.phone || "-"}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{r.companyPhone || "-"}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{r.company || "-"}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{r.position || "-"}</TableCell>
                                        </>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </ResizableTable>
            </Paper>

            {/* 페이지 이동 — 표 아래 가운데(전 시스템 공통 규칙). 스크롤 영역 안이라 목록과 함께 흐른다. */}
            <TablePaginator
                total={total}
                pageSize={size}
                page={page + 1}
                onPageChange={(p) => { const np = p - 1; setPage(np); load(q, np); }}
            />

            <ContactEditDialog
                open={Boolean(editDlg)}
                initial={editDlg}
                // 신규 등록({scope} 만 있는 객체)은 당연히 편집 가능. 기존 행은 ownedByMe 를 따른다.
                readOnly={Boolean(editDlg?.id) && !editDlg?.ownedByMe}
                onClose={() => setEditDlg(null)}
                onSaved={async () => { setEditDlg(null); await load(); }}
                // 주소를 눈으로 읽고 작성 화면에서 다시 타이핑하는 왕복을 없앤다.
                // 모달을 닫고 곧바로 메일 작성으로 넘어간다(둘이 겹쳐 뜨면 어느 쪽이 활성인지 헷갈린다).
                onComposeMail={onComposeMail ? (c) => {
                    setEditDlg(null);
                    onComposeMail({
                        tos: [c.name ? `${c.name} <${c.email}>` : c.email],
                        subject: "",
                    });
                } : undefined}
            />

            {/* 사내연락처 탭의 행 상세 — 조회 전용이라 onSaved 짝이 없다. */}
            <CompanyMemberDialog
                open={Boolean(memberDlg)}
                member={memberDlg}
                onClose={() => setMemberDlg(null)}
                onComposeMail={onComposeMail ? (m) => {
                    setMemberDlg(null);
                    onComposeMail({
                        tos: [m.name ? `${m.name} <${m.email}>` : m.email],
                        subject: "",
                    });
                } : undefined}
            />
        </Box>
    );
}
